import { MoreTime as MoreTimeIcon } from 'components/icons';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { SwitchMatterModalResult } from 'components/modals/invoices/SwitchMatterModal.tsx';
import { useModal } from 'components/modals/useModal.ts';
import { FORM_OVERFLOW_CUSTOM_STYLE } from 'components/modals/util.tsx';
import { useInvoice } from 'pages/invoices/hooks/useInvoice.ts';
import { DetailsTabSuccessMessages } from 'pages/invoices/InvoicePage.tsx';
import { getMultiplierTypeOptions } from 'pages/invoices/util/InvoicesPageUtil';
import TimecardsBulkSelectModal from 'pages/timecards/components/TimecardsBulkSelectModal.tsx';
import { timecardSecondsToHours } from 'pages/timecards/utils/timecardUtils';
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { Control, UseFormGetValues, UseFormHandleSubmit, UseFormReset, UseFormSetValue } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'store';
import { useCopyInvoiceTimecardMutation, useUpdateInvoiceTimecardsMutation } from 'store/api/invoice-details';
import { useGetTenantConfigurationQuery } from 'store/api/tenants';
import { disableButtons } from 'store/reducers/buttons';
import { ClassifierData } from 'types/classifiers';
import {
    InvoiceMatterForm,
    InvoiceMatterSummary,
    InvoiceTimecardAssignment,
    InvoiceTimecardBulkUpdateDto,
    InvoiceTimecardForm,
} from 'types/invoice-details';
import { EditIntentTypeCode, InvoiceData, InvoiceStatusType } from 'types/invoices';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { cn } from 'utils/cn';
import { createTabState } from 'utils/tabStateUtil.ts';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState.ts';
import { usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import InvoiceLoader from '../../components/InvoiceLoader';
import { ItemActionButton } from '../../components/ItemActionButton';
import TimecardActions from '../../components/TimecardActions';
import { AdvancedBillingSwitch, ItemTableCard, SECTION_TITLE } from '../SectionCard';
import { CasePricelistRangeTooltip } from './CasePricelistRangeTooltip';
import InvoiceAssignmentTimecardHeader from './InvoiceAssignmentTimecardHeader';
import InvoiceTimecardAssignmentTable from './InvoiceTimecardAssignmentTable';
import InvoiceTimecardTableTotals, { TimecardSortState } from './InvoiceTimecardTableTotals';
import { DIALOG_560 } from 'components/modals/dialogWidths';

export interface InvoiceTimecardTableProps {
    addedInvoiceTimecardIds: number[];
    setAddedInvoiceTimecardIds: Dispatch<SetStateAction<number[]>>;
    setSuccessMessages: Dispatch<SetStateAction<DetailsTabSuccessMessages>>;
    successMessages: DetailsTabSuccessMessages;
    isInViewMode: boolean;
    invoice: InvoiceData;
    isLoading: boolean;
    classifiers: Record<string, ClassifierData>;
    validateForm: UseFormHandleSubmit<InvoiceMatterForm>;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    reset: UseFormReset<InvoiceMatterForm>;
    control: Control<InvoiceMatterForm>;
    invoiceMatter: InvoiceMatterSummary;
}

type Props = InvoiceTimecardTableProps;

export const DEFAULT_SORT: TimecardSortState = { field: 'date', direction: 'asc' };

function getAssignmentSortKey(assignment: InvoiceTimecardAssignment): string {
    return String(assignment.assignmentId ?? 'no-assignment');
}

export function buildElementKey(assignment?: InvoiceTimecardAssignment) {
    return assignment?.assignmentId ? `add-timecard-btn-${assignment.assignmentId}` : `add-timecard-btn-wo-assignment`;
}

interface AddTimecardButtonProps {
    assignment?: InvoiceTimecardAssignment;
    isInViewMode: boolean;
    canCreateTimecards: boolean;
    buttonsDisabled: boolean;
    invoiceId: number;
    matterId: number;
    navigateWithReturn: ReturnType<typeof useNavigateWithReturnState>;
}

const AddTimecardButton = React.memo(function AddTimecardButton({
    assignment,
    isInViewMode,
    canCreateTimecards,
    buttonsDisabled,
    invoiceId,
    matterId,
    navigateWithReturn,
}: AddTimecardButtonProps) {
    return (
        <div className="m-2 inline-flex">
            <ItemActionButton
                tooltip={t('invoices.tooltips.add-new-timecard')}
                disabled={isInViewMode || !canCreateTimecards || buttonsDisabled}
                onClick={() =>
                    navigateWithReturn(
                        assignment?.assignmentId
                            ? `/invoice/${invoiceId}/matter/${matterId}/assignment/${assignment?.assignmentId}/timecards/new`
                            : `/invoice/${invoiceId}/matter/${matterId}/timecards/new`,
                        {
                            state: {
                                returnTo: `/invoice/${invoiceId}#${buildElementKey(assignment)}`,
                                ...createTabState('invoices-center-block', 'details'),
                            },
                        }
                    )
                }
            >
                <MoreTimeIcon />
            </ItemActionButton>
        </div>
    );
});

type TableArgs = Pick<
    Props,
    | 'invoice'
    | 'invoiceMatter'
    | 'classifiers'
    | 'getValues'
    | 'setValue'
    | 'setAddedInvoiceTimecardIds'
    | 'setSuccessMessages'
>;

/** A case's timecard list: its selection, its options and its bulk actions, as both designs' lists use them. */
export const useInvoiceTimecardTable = ({
    invoice,
    invoiceMatter,
    classifiers,
    getValues,
    setValue,
    setAddedInvoiceTimecardIds,
    setSuccessMessages,
}: TableArgs) => {
    const { openModal, closeModal } = useModal();
    const [advancedBillingEnabled, setAdvancedBillingEnabled] = useState(false);
    const [openModalForTimecardId, setOpenModalForTimecardId] = useState<number | null>(null);
    const navigateWithReturn = useNavigateWithReturnState();
    const canCreateTimecards = useHasPermissions(['CREATE.TIMECARDS']);
    const { isDebit, isCorrection: isCorrectionInvoice, isCredit, isSent, isPaid } = useInvoice(invoice);
    const dispatch = useDispatch();
    const buttonsDisabled = useSelector((state) => state.buttons.buttonsDisabled);
    const { data: tenantConfiguration } = useGetTenantConfigurationQuery();
    const nonBillableBgColor = tenantConfiguration?.invoiceNonBillableTcBgColor;
    const multiplierTypes = useMemo(() => getMultiplierTypeOptions(classifiers), [classifiers]);
    const { isTwoDecimalDisplayEnabled, isTwoDecimalEntryEnabled, displayDecimalScale } = useTimecardPrecision();
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);

    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    const toggleSelection = useCallback((id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const { hasBillableSelected, hasNonBillableSelected } = useMemo(() => {
        const selectedTimecards = invoiceMatter.invoiceTimecards.filter((tc) => selectedIds.has(tc.id));
        return {
            hasBillableSelected: selectedTimecards.some((tc) => !tc.isNonBillable),
            hasNonBillableSelected: selectedTimecards.some((tc) => tc.isNonBillable),
        };
    }, [invoiceMatter.invoiceTimecards, selectedIds]);

    const clearModalRequest = useCallback(() => {
        setOpenModalForTimecardId(null);
    }, []);

    const invoiceTimecardTotalCount = invoiceMatter.invoiceAssignments.reduce((total, assignment) => {
        if (assignment.timeCardDetails && Array.isArray(assignment.timeCardDetails)) {
            return total + assignment.timeCardDetails.length;
        }
        return total;
    }, 0);

    const { id: invoiceId, hasCreditInvoice, isSplit, invoiceStatusTypeCode } = invoice;
    const { matterId } = invoiceMatter;

    const isInProcessingState = [
        InvoiceStatusType.DRAFT,
        InvoiceStatusType.REVIEWED,
        InvoiceStatusType.APPROVED,
        InvoiceStatusType.FINALIZED,
    ]
        .map((status) => status.toString())
        .includes(invoiceStatusTypeCode);

    const allowsNewTimecards = useMemo(
        () => (isCorrectionInvoice || (isDebit && isInProcessingState)) && !hasCreditInvoice && !isSplit,
        [isDebit, isCorrectionInvoice, isInProcessingState, hasCreditInvoice, isSplit]
    );

    const [updateInvoiceTimecards] = useUpdateInvoiceTimecardsMutation();
    const [copyInvoiceTimecard] = useCopyInvoiceTimecardMutation();

    const handleDuplicate = useCallback(() => {
        const firstSelectedId = selectedIds.values().next().value;
        if (firstSelectedId != null) {
            copyInvoiceTimecard({ invoiceId, matterId, id: firstSelectedId })
                .unwrap()
                .then((newTimecard) => {
                    setAddedInvoiceTimecardIds((prev) => [...prev, newTimecard.timecard.id]);
                    setSuccessMessages((prev) => ({ ...prev, newTimecardsAdded: true }));
                    setOpenModalForTimecardId(newTimecard.timecard.id);
                })
                .catch(console.error);
        }
    }, [selectedIds, copyInvoiceTimecard, invoiceId, matterId, setAddedInvoiceTimecardIds, setSuccessMessages]);

    // An empty bulk price leaves each line's price, set or not, as it is: only the price change sends one.
    const handleUpdateInvoiceTimecards = useCallback(
        (idList: number[], updatedTimecards: InvoiceTimecardForm[], typedHourlyPrice: number | null = null) => {
            const dtoList: InvoiceTimecardBulkUpdateDto[] = [];
            updatedTimecards
                .filter((tc) => idList.includes(tc.id))
                .forEach((tc) =>
                    dtoList.push({
                        id: tc.id,
                        remunerationCalcTypeCode: tc.remunerationCalcType,
                        billedTimeInSeconds: tc.billedTimeInSeconds,
                        hourlyPrice: typedHourlyPrice,
                        manualAdjustmentAmount: tc.manualAdjustmentAmount,
                        newMatterId: tc.newMatter?.id ?? null,
                        editIntentTypeCode: tc.editIntentTypeCode,
                        isNonBillable: tc.isNonBillable,
                        preserveNonBillableHours: tc.preserveNonBillableHours,
                        newMatterAssignmentId: tc.newMatterAssignment?.id ?? null,
                    })
                );

            return updateInvoiceTimecards({ invoiceId, matterId, dtoList }).unwrap().catch(console.error);
        },
        [updateInvoiceTimecards, invoiceId, matterId]
    );

    const handleMove = async (value: SwitchMatterModalResult) => {
        dispatch(disableButtons(true));
        await new Promise((resolve) => setTimeout(resolve, 0));

        const invoiceTimecards: InvoiceTimecardForm[] = getValues('invoiceTimecards');
        const idList: number[] = [];
        // A move to the current case is still sent as a move so the backend applies the
        // selected (or intentionally emptied) assignment instead of treating it as a plain edit.
        const newMatter = value.matter ? { id: value.matter.id, name: value.matter.fullName } : null;
        const updatedTimecards = invoiceTimecards.map((invoiceTimecard) => {
            if (invoiceTimecard.id && selectedIds.has(invoiceTimecard.id)) {
                idList.push(invoiceTimecard.id);
                return {
                    ...invoiceTimecard,
                    newMatter,
                    newMatterAssignment: value.matterAssignment,
                    editIntentTypeCode: newMatter ? EditIntentTypeCode.MOVE_TO_ANOTHER_CASE : null,
                };
            }
            return invoiceTimecard;
        });
        const requestPromise = handleUpdateInvoiceTimecards(idList, updatedTimecards);
        setValue('invoiceTimecards', updatedTimecards, { shouldDirty: true });
        await requestPromise;
        clearSelection();
    };

    const handleNonBillable = async () => {
        dispatch(disableButtons(true));
        await new Promise((resolve) => setTimeout(resolve, 0));

        const invoiceTimecards: InvoiceTimecardForm[] = getValues('invoiceTimecards');
        const idList: number[] = [];
        const updatedTimecards = invoiceTimecards.map((invoiceTimecard) => {
            if (invoiceTimecard.id && selectedIds.has(invoiceTimecard.id)) {
                idList.push(invoiceTimecard.id);
                return {
                    ...invoiceTimecard,
                    isNonBillable: true,
                    billedTimeInHours: invoiceTimecard.preserveNonBillableHours
                        ? invoiceTimecard.billedTimeInHours
                        : '0',
                    editIntentTypeCode: EditIntentTypeCode.MARK_AS_NONBILLABLE,
                };
            }
            return invoiceTimecard;
        });
        const requestPromise = handleUpdateInvoiceTimecards(idList, updatedTimecards);
        setValue('invoiceTimecards', updatedTimecards, { shouldDirty: true });
        await requestPromise;
        clearSelection();
    };

    const handleBillable = async () => {
        dispatch(disableButtons(true));
        await new Promise((resolve) => setTimeout(resolve, 0));

        const invoiceTimecards: InvoiceTimecardForm[] = getValues('invoiceTimecards');
        const idList: number[] = [];
        const updatedTimecards = invoiceTimecards.map((invoiceTimecard) => {
            if (invoiceTimecard.id && selectedIds.has(invoiceTimecard.id)) {
                idList.push(invoiceTimecard.id);
                const totalTimeInSeconds = invoiceTimecard.totalTimeInSeconds ?? 0;
                return {
                    ...invoiceTimecard,
                    isNonBillable: false,
                    preserveNonBillableHours: false,
                    billedTimeInSeconds: totalTimeInSeconds,
                    billedTimeInHours: String(timecardSecondsToHours(totalTimeInSeconds, isTwoDecimalEntryEnabled)),
                };
            }
            return invoiceTimecard;
        });
        const requestPromise = handleUpdateInvoiceTimecards(idList, updatedTimecards);
        setValue('invoiceTimecards', updatedTimecards, { shouldDirty: true });
        await requestPromise;
        clearSelection();
    };

    const handleChangePrice = async (value: number | null) => {
        if (value != null) {
            dispatch(disableButtons(true));
            await new Promise((resolve) => setTimeout(resolve, 0));

            const invoiceTimecards: InvoiceTimecardForm[] = getValues('invoiceTimecards');
            const idList: number[] = [];
            const updatedTimecards = invoiceTimecards.map((invoiceTimecard) => {
                if (invoiceTimecard.id && selectedIds.has(invoiceTimecard.id)) {
                    idList.push(invoiceTimecard.id);
                    return {
                        ...invoiceTimecard,
                        hourlyPrice: value,
                    };
                }
                return invoiceTimecard;
            });
            const requestPromise = handleUpdateInvoiceTimecards(idList, updatedTimecards, value);
            setValue('invoiceTimecards', updatedTimecards, { shouldDirty: true });
            await requestPromise;
            clearSelection();
        }
    };

    const handlePostpone = async () => {
        dispatch(disableButtons(true));
        await new Promise((resolve) => setTimeout(resolve, 0));

        const invoiceTimecards: InvoiceTimecardForm[] = getValues('invoiceTimecards');
        const idList: number[] = [];
        const updatedTimecards = invoiceTimecards.map((invoiceTimecard) => {
            if (invoiceTimecard.id && selectedIds.has(invoiceTimecard.id)) {
                idList.push(invoiceTimecard.id);
                return {
                    ...invoiceTimecard,
                    editIntentTypeCode: EditIntentTypeCode.POSTPONE,
                };
            }
            return invoiceTimecard;
        });
        const requestPromise = handleUpdateInvoiceTimecards(idList, updatedTimecards);
        setValue('invoiceTimecards', updatedTimecards, { shouldDirty: true });
        await requestPromise;
        clearSelection();
    };

    const handleRemove = async () => {
        dispatch(disableButtons(true));
        await new Promise((resolve) => setTimeout(resolve, 0));

        const invoiceTimecards: InvoiceTimecardForm[] = getValues('invoiceTimecards');
        const idList: number[] = [];
        const updatedTimecards = invoiceTimecards.map((invoiceTimecard) => {
            if (invoiceTimecard.id && selectedIds.has(invoiceTimecard.id)) {
                idList.push(invoiceTimecard.id);
                return {
                    ...invoiceTimecard,
                    editIntentTypeCode: EditIntentTypeCode.REMOVE_VIA_CORRECTION,
                };
            }
            return invoiceTimecard;
        });
        const requestPromise = handleUpdateInvoiceTimecards(idList, updatedTimecards);
        setValue('invoiceTimecards', updatedTimecards, { shouldDirty: true });
        await requestPromise;
        clearSelection();
    };

    const onBulkSelect = useCallback((isToSelect: boolean, timecardIds: number[]) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            timecardIds.forEach((id) => (isToSelect ? next.add(id) : next.delete(id)));
            return next;
        });
    }, []);

    const handleBulkSelect = () => {
        openModal(
            <TimecardsBulkSelectModal
                onBulkSelect={(ids) => onBulkSelect(true, ids)}
                onBulkUnselect={(ids) => onBulkSelect(false, ids)}
                onClose={closeModal}
                timecards={invoiceMatter.invoiceTimecards.map((tc) => ({
                    ...tc,
                    isSelectable: tc.editIntentTypeCode == null,
                }))}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE,
            undefined,
            DIALOG_560
        );
    };

    return {
        advancedBillingEnabled,
        setAdvancedBillingEnabled,
        openModalForTimecardId,
        clearModalRequest,
        navigateWithReturn,
        canCreateTimecards,
        isCorrectionInvoice,
        isCredit,
        isSent,
        isPaid,
        buttonsDisabled,
        nonBillableBgColor,
        multiplierTypes,
        isTwoDecimalDisplayEnabled,
        isTwoDecimalEntryEnabled,
        displayDecimalScale,
        isPaymentCurrencyMode,
        displayCurrency,
        selectedIds,
        toggleSelection,
        clearSelection,
        onBulkSelect,
        hasBillableSelected,
        hasNonBillableSelected,
        invoiceTimecardTotalCount,
        invoiceId,
        matterId,
        allowsNewTimecards,
        handleDuplicate,
        handleUpdateInvoiceTimecards,
        handleMove,
        handleNonBillable,
        handleBillable,
        handleChangePrice,
        handlePostpone,
        handleRemove,
        handleBulkSelect,
    };
};

const InvoiceTimecardTable = ({
    addedInvoiceTimecardIds,
    setAddedInvoiceTimecardIds,
    setSuccessMessages,
    isInViewMode,
    invoice,
    isLoading,
    classifiers,
    validateForm,
    setValue,
    getValues,
    reset,
    control,
    invoiceMatter,
}: Props) => {
    const {
        advancedBillingEnabled,
        setAdvancedBillingEnabled,
        openModalForTimecardId,
        clearModalRequest,
        navigateWithReturn,
        canCreateTimecards,
        isCorrectionInvoice,
        isCredit,
        isSent,
        isPaid,
        buttonsDisabled,
        nonBillableBgColor,
        multiplierTypes,
        isTwoDecimalDisplayEnabled,
        isTwoDecimalEntryEnabled,
        displayDecimalScale,
        isPaymentCurrencyMode,
        displayCurrency,
        selectedIds,
        toggleSelection,
        hasBillableSelected,
        hasNonBillableSelected,
        invoiceTimecardTotalCount,
        invoiceId,
        matterId,
        allowsNewTimecards,
        handleDuplicate,
        handleUpdateInvoiceTimecards,
        handleMove,
        handleNonBillable,
        handleBillable,
        handleChangePrice,
        handlePostpone,
        handleRemove,
        handleBulkSelect,
    } = useInvoiceTimecardTable({
        invoice,
        invoiceMatter,
        classifiers,
        getValues,
        setValue,
        setAddedInvoiceTimecardIds,
        setSuccessMessages,
    });
    const [sortStates, setSortStates] = useState<Record<string, TimecardSortState>>({});
    const handleSortChange = useCallback((assignmentKey: string, state: TimecardSortState) => {
        setSortStates((prev) => ({ ...prev, [assignmentKey]: state }));
    }, []);

    if (isLoading) {
        return <InvoiceLoader />;
    }

    if (invoiceTimecardTotalCount === 0 && allowsNewTimecards) {
        return (
            <div className="flex flex-col items-start">
                <AddTimecardButton
                    isInViewMode={isInViewMode}
                    canCreateTimecards={canCreateTimecards}
                    buttonsDisabled={buttonsDisabled}
                    invoiceId={invoiceId}
                    matterId={matterId}
                    navigateWithReturn={navigateWithReturn}
                />
            </div>
        );
    }

    return (
        <ItemTableCard
            title={
                <div className="flex min-w-0 items-center whitespace-nowrap">
                    <p className={SECTION_TITLE}>
                        <FormattedMessage
                            id="invoices.details.timecards"
                            values={{ count: invoiceTimecardTotalCount }}
                        />
                    </p>
                    <CasePricelistRangeTooltip
                        matterId={invoiceMatter.matterId}
                        rates={invoiceMatter.rates}
                        stored={invoiceMatter}
                        pricelistCurrencyTypeCode={invoiceMatter.pricelistCurrencyTypeCode}
                    />
                    {invoiceMatter.pricelistName ? (
                        <span className="ml-4 min-w-0">
                            <EllipsisTooltip>
                                <FormattedMessage id="invoices.details.assignments.price-list" />
                                {`: ${invoiceMatter.pricelistName}`}
                            </EllipsisTooltip>
                        </span>
                    ) : null}
                </div>
            }
            actions={
                <>
                    <AdvancedBillingSwitch
                        checked={advancedBillingEnabled}
                        onCheckedChange={setAdvancedBillingEnabled}
                        disabled={isInViewMode}
                    />
                    {!isInViewMode || isCorrectionInvoice ? (
                        <TimecardActions
                            handleMoveTimecard={handleMove}
                            handleNonBillable={() => {
                                void handleNonBillable();
                            }}
                            handleBillable={() => {
                                void handleBillable();
                            }}
                            handlePostpone={() => {
                                void handlePostpone();
                            }}
                            handleDuplicate={handleDuplicate}
                            handleRemove={() => {
                                void handleRemove();
                            }}
                            handleChangePrice={handleChangePrice}
                            handleBulkSelect={handleBulkSelect}
                            selectedIds={selectedIds}
                            hasBillableSelected={hasBillableSelected}
                            hasNonBillableSelected={hasNonBillableSelected}
                            getValues={getValues}
                            invoiceId={invoiceId}
                            matterId={matterId}
                            invoiceAllowsNewTimecards={allowsNewTimecards}
                            userCanCreateTimecards={canCreateTimecards}
                            isCorrectionInvoice={isCorrectionInvoice}
                            invoice={invoice}
                        />
                    ) : null}
                </>
            }
        >
            {invoiceMatter.invoiceAssignments
                .toSorted((a, b) =>
                    (a.assignmentName ?? '').localeCompare(b.assignmentName ?? '', undefined, { numeric: true })
                )
                .map(
                    (assignment) =>
                        !!assignment.timeCardDetails?.length && (
                            <div key={assignment.assignmentId} id={buildElementKey(assignment)}>
                                <InvoiceAssignmentTimecardHeader
                                    isInViewMode={isInViewMode}
                                    index={invoiceMatter.invoiceAssignments.indexOf(assignment)}
                                    assignment={assignment}
                                    invoice={invoice}
                                    getValues={getValues}
                                    setValue={setValue}
                                    control={control}
                                    invoiceMatter={invoiceMatter}
                                />
                                <InvoiceTimecardTableTotals
                                    assignment={assignment}
                                    currency={displayCurrency}
                                    sortState={sortStates[getAssignmentSortKey(assignment)] ?? DEFAULT_SORT}
                                    onSortChange={(state) => handleSortChange(getAssignmentSortKey(assignment), state)}
                                    isPaymentCurrencyMode={isPaymentCurrencyMode}
                                />
                                {/* The add button, when shown, spaces the list from the card's edge; otherwise the padding does. */}
                                <div className={cn(!allowsNewTimecards && 'pb-5')}>
                                    <InvoiceTimecardAssignmentTable
                                        assignment={assignment}
                                        advancedBillingEnabled={advancedBillingEnabled}
                                        sortState={sortStates[getAssignmentSortKey(assignment)] ?? DEFAULT_SORT}
                                        isInViewMode={isInViewMode}
                                        invoice={invoice}
                                        invoiceId={invoiceId}
                                        addedInvoiceTimecardIds={addedInvoiceTimecardIds}
                                        matterAssignments={invoiceMatter.matterAssignments}
                                        control={control}
                                        validateForm={validateForm}
                                        setValue={setValue}
                                        getValues={getValues}
                                        reset={reset}
                                        classifiers={classifiers}
                                        handleUpdateInvoiceTimecards={handleUpdateInvoiceTimecards}
                                        openModalForTimecardId={openModalForTimecardId}
                                        clearModalRequest={clearModalRequest}
                                        nonBillableBgColor={nonBillableBgColor}
                                        isCredit={isCredit}
                                        isCorrectionInvoice={isCorrectionInvoice}
                                        isSent={isSent}
                                        isPaid={isPaid}
                                        multiplierTypes={multiplierTypes}
                                        buttonsDisabled={buttonsDisabled}
                                        isTwoDecimalDisplayEnabled={isTwoDecimalDisplayEnabled}
                                        isTwoDecimalEntryEnabled={isTwoDecimalEntryEnabled}
                                        displayDecimalScale={displayDecimalScale}
                                        selectedIds={selectedIds}
                                        toggleSelection={toggleSelection}
                                    />
                                </div>
                                {allowsNewTimecards && (
                                    <AddTimecardButton
                                        assignment={assignment}
                                        isInViewMode={isInViewMode}
                                        canCreateTimecards={canCreateTimecards}
                                        buttonsDisabled={buttonsDisabled}
                                        invoiceId={invoiceId}
                                        matterId={matterId}
                                        navigateWithReturn={navigateWithReturn}
                                    />
                                )}
                            </div>
                        )
                )}
        </ItemTableCard>
    );
};

export default InvoiceTimecardTable;
